import {
  DocumentData,
  QueryDocumentSnapshot,
  QueryFieldFilterConstraint,
  and,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import Failure from '../../../core/Failure';
import { FirestoreCollections, firestore } from '../../../firebase.config';
import Subscription from '../../models/Subscription';
import Job from '../../models/Job';
import deleteJob from '../../../domain/use_cases/jobs/deleteJob';
import JobWithContact from '../../models/@aggregates/JobWithContact';
import { IContact } from '@leedpulse-monorepo/interfaces';
import { JobType } from '@leedpulse-monorepo/enums';
import countScheduledMessages from './countScheduledMessages';

// The scheduled messages only: the contact workflow jobs of Jobs (type workflow) are not messages.
// Filtered in the query, so that every page is full (indexes Jobs: [id_api_key, [id_contact,]] timestamp, type)
const notWorkflowJob = (): QueryFieldFilterConstraint => where('type', '!=', JobType.workflow);

async function getJobWithContact(job: Job): Promise<JobWithContact> {
  try {
    const db = firestore;
    const contactDoc = await getDoc(
      doc(db, FirestoreCollections.contacts, job.idContact)
    );
    if (!contactDoc.exists()) {
      // Delete the job if the contact does not exist
      // We don't want to keep jobs without related contacts
      await deleteJob(job);
      throw new GetJobsFailure();
    }
    const contact = contactDoc.data() as IContact;
    return new JobWithContact(job, contact);
  } catch (e) {
    console.log('error: ', e);
    throw new GetJobsFailure();
  }
}

async function getJobs(
  offset: number,
  lastVisible?: QueryDocumentSnapshot<DocumentData, DocumentData>,
  scopedSubscriptionId?: string | null,
  scopedContactId?: string | null
): Promise<{
  jobs: JobWithContact[];
  lastVisible: QueryDocumentSnapshot<DocumentData, DocumentData>;
  totalCollectionCount: number;
}> {
  try {
    const db = firestore;
    const jobsCollection = collection(db, FirestoreCollections.jobs);
    if (scopedSubscriptionId) {
      // We first get the corresponding subscription
      const subscriptionDoc = await getDoc(
        doc(db, FirestoreCollections.subscriptions, scopedSubscriptionId)
      );
      if (!subscriptionDoc.exists()) {
        return {
          jobs: [],
          lastVisible: {} as QueryDocumentSnapshot<DocumentData, DocumentData>,
          totalCollectionCount: 0,
        };
      }
      const subscription = Subscription.fromFirestore(subscriptionDoc);

      // Then we get the jobs
      const results = lastVisible
        ? await getDocs(
            query(
              jobsCollection,
              where('id_api_key', '==', subscription.apiKey),
              notWorkflowJob(),
              orderBy('timestamp'),
              startAfter(lastVisible),
              limit(offset)
            )
          )
        : await getDocs(
            query(
              jobsCollection,
              where('id_api_key', '==', subscription.apiKey),
              notWorkflowJob(),
              orderBy('timestamp'),
              limit(offset)
            )
          );

      const jobs: JobWithContact[] = await Promise.all(
        results.docs.map(async (doc) => {
          return await getJobWithContact(Job.fromFirestore(doc));
        })
      );

      const totalCollectionCount = await countScheduledMessages([where('id_api_key', '==', subscription.apiKey)]);

      return {
        jobs,
        lastVisible: results.docs[results.docs.length - 1],
        totalCollectionCount,
      };
    } else if (scopedContactId) {
      const contactDoc = await getDoc(
        doc(db, FirestoreCollections.contacts, scopedContactId)
      );
      if (!contactDoc.exists()) {
        return {
          jobs: [],
          lastVisible: {} as QueryDocumentSnapshot<DocumentData, DocumentData>,
          totalCollectionCount: 0,
        };
      }
      const contact = contactDoc.data() as IContact;

      // Then we get the jobs
      const results = lastVisible
        ? await getDocs(
            query(
              jobsCollection,
              and(
                where('id_contact', '==', contact.id),
                where('id_api_key', '==', contact.id_api_key),
                notWorkflowJob()
              ),
              orderBy('timestamp'),
              startAfter(lastVisible),
              limit(offset)
            )
          )
        : await getDocs(
            query(
              jobsCollection,
              and(
                where('id_contact', '==', contact.id),
                where('id_api_key', '==', contact.id_api_key),
                notWorkflowJob()
              ),
              orderBy('timestamp'),
              limit(offset)
            )
          );

      const jobs: JobWithContact[] = await Promise.all(
        results.docs.map(async (doc) => {
          return await getJobWithContact(Job.fromFirestore(doc));
        })
      );

      const totalCollectionCount = await countScheduledMessages([
        where('id_contact', '==', contact.id),
        where('id_api_key', '==', contact.id_api_key),
      ]);

      return {
        jobs,
        lastVisible: results.docs[results.docs.length - 1],
        totalCollectionCount,
      };
    } else {
      const results = lastVisible
        ? await getDocs(
            query(
              jobsCollection,
              notWorkflowJob(),
              orderBy('timestamp'),
              startAfter(lastVisible),
              limit(offset)
            )
          )
        : await getDocs(
            query(jobsCollection, notWorkflowJob(), orderBy('timestamp'), limit(offset))
          );

      const jobs: JobWithContact[] = await Promise.all(
        results.docs.map(async (doc) => {
          return await getJobWithContact(Job.fromFirestore(doc));
        })
      );

      const totalCollectionCount = await countScheduledMessages();

      return {
        jobs,
        lastVisible: results.docs[results.docs.length - 1],
        totalCollectionCount,
      };
    }
  } catch (e) {
    console.log('error: ', e);
    throw new GetJobsFailure();
  }
}

export class GetJobsFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

export default getJobs;
